import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Link from '@fuse/core/Link';
import { apiClient } from '@/api/client';

const schema = z.object({
	userName: z.string().trim().nonempty('Please enter your username.')
});

type FormType = z.infer<typeof schema>;

/**
 * Forgot-password request page. Collects the username and asks the API to send a reset
 * email. The backend deliberately returns a generic success message regardless of whether
 * the user exists, so this UI does the same — no UX path can leak account existence.
 */
function ForgotPasswordView() {
	const [submitted, setSubmitted] = useState(false);

	const { control, handleSubmit, formState } = useForm<FormType>({
		mode: 'onChange',
		defaultValues: { userName: '' },
		resolver: zodResolver(schema)
	});

	const mutation = useMutation({
		mutationFn: async ({ userName }: FormType) => {
			const res = await apiClient.forgotPassword({ userName });
			return res.data;
		},
		onSuccess: () => {
			setSubmitted(true);
		}
	});

	const onSubmit = (data: FormType) => mutation.mutate(data);

	return (
		<div className="flex min-w-0 flex-auto flex-col items-center sm:justify-center">
			<Paper className="min-h-full w-full rounded-none px-4 py-8 sm:min-h-auto sm:w-auto sm:rounded-xl sm:p-12 sm:shadow-sm">
				<Box className="mx-auto flex w-full max-w-80 flex-col gap-6 sm:mx-0 sm:w-80">
					<div className="w-full">
						<img
							className="w-12"
							src="/assets/images/logo/TSELogo.png"
							alt="logo"
						/>
						<Typography className="mt-8 text-4xl leading-[1.25] font-extrabold tracking-tight">
							Forgot your password?
						</Typography>
						<Typography
							className="mt-2"
							color="text.secondary"
						>
							Enter your username and we'll send a reset link to the email on file.
						</Typography>
					</div>

					{submitted ? (
						<>
							<Alert severity="success">
								If that account exists, a password reset link has been sent. Check your inbox.
							</Alert>
							<Link
								className="text-md font-medium"
								to="/sign-in"
							>
								Back to sign in
							</Link>
						</>
					) : (
						<form
							className="flex w-full flex-col gap-4"
							onSubmit={handleSubmit(onSubmit)}
							noValidate
						>
							<Controller
								name="userName"
								control={control}
								render={({ field, fieldState }) => (
									<TextField
										{...field}
										label="Username"
										autoFocus
										fullWidth
										variant="outlined"
										required
										error={!!fieldState.error}
										helperText={fieldState.error?.message}
									/>
								)}
							/>
							{mutation.isError && (
								<Alert severity="error">Something went wrong. Please try again.</Alert>
							)}
							<Button
								type="submit"
								variant="contained"
								color="secondary"
								size="large"
								disabled={!formState.isValid || mutation.isPending}
							>
								{mutation.isPending ? 'Sending…' : 'Send reset link'}
							</Button>
							<Link
								className="text-md self-center font-medium"
								to="/sign-in"
							>
								Back to sign in
							</Link>
						</form>
					)}
				</Box>
			</Paper>
		</div>
	);
}

export default ForgotPasswordView;
